/* ==========================================================================
   A postal code, confirmed out loud

   Five digits is the least a person can type to say where they are, and the
   easiest thing in the world to fat-finger. So it answers back: type 10952
   and it says Monsey, NY. Somebody who meant 10592 sees Ossining and stops.

   The lookup is a plain fact about the postal system, so it needs no account
   and nothing is stored until the form is saved.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { Field } from './ui';
import { api } from '../api';

interface Props {
  value: string;
  onChange: (zip: string) => void;
  label: string;
  hint?: string;
}

export function ZipField({ value, onChange, label, hint }: Props) {
  const [place, setPlace] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const digits = (value ?? '').replace(/[^0-9]/g, '').slice(0, 5);

  useEffect(() => {
    if (digits.length !== 5) {
      setPlace(null);
      return;
    }
    let alive = true;
    setChecking(true);
    api.zip(digits)
      .then((r) => { if (alive) setPlace(r.ok ? r.place + ', ' + r.state : ''); })
      .catch(() => { if (alive) setPlace(null); })
      .finally(() => { if (alive) setChecking(false); });
    /* Ignoring a stale answer matters here: somebody typing 10952 fires a
       lookup for 1, 10, 109 and so on, and those can land out of order. */
    return () => { alive = false; };
  }, [digits]);

  return (
    <Field label={label} hint={hint}>
      <input
        className="input"
        value={value ?? ''}
        placeholder="10952"
        inputMode="numeric"
        maxLength={10}
        onChange={(e) => onChange(e.target.value)}
      />
      {digits.length === 5 && !checking && place !== null && (
        <span className={place ? 'hint' : 'hint warn'}>
          {place || 'We do not know that postal code. Check the digits.'}
        </span>
      )}
    </Field>
  );
}
